/**
 * Decimal places a currency actually settles in — the frontend mirror of the runtime's
 * `CURRENCY_MINOR_UNITS`. Money travels at 8dp, but COP/CLP/PYG have no cents and a rail rejects an amount
 * that carries them, so an input must not let the user type one and a preview must not render `950,00 COP`
 * for a figure the backend quantized to `950`.
 *
 * Keep in sync with `coloca-nest/src/coloca/payments/constants/currency-minor-units.constant.ts`.
 */
const CURRENCY_MINOR_UNITS: Readonly<Record<string, number>> = {
  COP: 0,
  CLP: 0,
  PYG: 0,
  USD: 2,
  MXN: 2,
  BRL: 2,
  PEN: 2,
  ARS: 2,
  GTQ: 2,
  CRC: 2,
  EUR: 2,
  USDT: 6,
};

/** Minor units for a currency code, defaulting to 2 for anything unlisted. */
export const minorUnitsFor = (currency: string): number => CURRENCY_MINOR_UNITS[currency.toUpperCase()] ?? 2;

/**
 * Formats a runtime decimal string at its currency's settlement precision, without a `Number` round-trip on
 * the way in: `Intl.NumberFormat` v3 formats decimal strings exactly, so an 8dp ledger amount keeps every
 * digit it is entitled to.
 */
export const formatCurrencyAmount = (
  value: string | number | null | undefined,
  currency: string,
  locale: string,
): string => {
  const digits = minorUnitsFor(currency);
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: true,
  });
  const amount = value ?? 0;
  return formatter.format((typeof amount === "string" ? amount.trim() || "0" : amount) as unknown as number);
};

/** Compact badge label: `1M` / `500k` / `750`, so quick amounts stay readable at 11px. */
export const formatCompactAmount = (value: string | number, locale: string): string => {
  const amount = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(amount)) return String(value);
  if (Math.abs(amount) >= 1_000_000) {
    return `${(amount / 1_000_000).toLocaleString(locale, { maximumFractionDigits: 1 })}M`;
  }
  if (Math.abs(amount) >= 1_000) {
    return `${(amount / 1_000).toLocaleString(locale, { maximumFractionDigits: 1 })}k`;
  }
  return amount.toLocaleString(locale, { maximumFractionDigits: 2 });
};
